import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { workSpaceId } from '../services/staticSlice';
import { useGetProfilesQuery, useUpdateWorkspaceMutation } from '../services/createProfile';
import { useGetGeneralSettingsQuery } from '../services/settingsSlice';
import { toast } from '../components/Toast/toast';

interface Workspace {
  _id: string; // MongoDB ID - used for switching workspaces
  workspace_id: string; // Display name of the workspace
  profile_description: string;
  created_on: string;
  created_at: string;
  active?: boolean;
  googleanalytics?: boolean;
  meta?: boolean;
  googleads?: boolean;
  shopify?: boolean;
  youtube?: boolean;
  customBigQuery?: boolean;
}

interface WorkspaceContextType {
  currentWorkspace: string | null; // Now MongoDB ObjectId
  workspaces: Workspace[];
  switchWorkspace: (workspaceId: string) => Promise<void>;
  refreshWorkspaces: () => void;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  getCurrentWorkspaceObjectId: () => string | null;
  getCurrentWorkspaceName: () => string | null;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [currentWorkspace, setCurrentWorkspace] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data: profilesData,
    refetch: refetchProfiles,
    isLoading: isProfilesLoading,
    error: profilesError
  } = useGetProfilesQuery({}, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: userSettings,
    isLoading: isSettingsLoading
  } = useGetGeneralSettingsQuery('', {
    refetchOnMountOrArgChange: true,
  });

  const [updateWorkspace, { isLoading: isUpdatingWorkspace, error: updateWorkspaceError }] = useUpdateWorkspaceMutation();

  // Initialize workspace from various sources
  const initializeWorkspace = useCallback(() => {
    try {
      // Priority order: 
      // 1. User settings from backend (current_workspace) - this is MongoDB _id
      // 2. First workspace from profiles
      // 3. LocalStorage fallback (legacy support)
      // 4. Demo workspace fallback

      let workspaceToSet: string | null = null;

      if (userSettings?.settings?.current_workspace && profilesData?.workspaces?.length > 0) {
        // Use the MongoDB _id from settings
        workspaceToSet = userSettings.settings.current_workspace;
      }

      if (!workspaceToSet && profilesData?.workspaces?.length > 0) {
        // Find active workspace or use first one - use MongoDB _id
        const activeWorkspace = profilesData.workspaces.find((ws: Workspace) => ws.active);
        workspaceToSet = activeWorkspace?._id || profilesData.workspaces[0]._id;
      }

      if (!workspaceToSet) {
        // Legacy fallback: check if localStorage has an ObjectId-like value
        const storedWorkspaceId = localStorage.getItem('workspace_id');
        if (storedWorkspaceId && storedWorkspaceId !== 'Demo' && storedWorkspaceId.length === 24) {
          // Looks like an ObjectId, use it
          workspaceToSet = storedWorkspaceId;
        } else {
          // Try to find workspace by name (legacy support)
          if (storedWorkspaceId && profilesData?.workspaces?.length > 0) {
            const foundWorkspace = profilesData.workspaces.find((ws: Workspace) => ws.workspace_id === storedWorkspaceId);
            workspaceToSet = foundWorkspace?._id || null;
          }
        }
      }

      if (!workspaceToSet) {
        // Final fallback to Demo
        workspaceToSet = 'Demo';
      }

      if (workspaceToSet && workspaceToSet !== currentWorkspace) {
        setCurrentWorkspace(workspaceToSet);
        localStorage.setItem('workspace_id', workspaceToSet);
        dispatch(workSpaceId(workspaceToSet));
      }

      setIsInitialized(true);
      setError(null);
    } catch (err) {
      console.error('Error initializing workspace:', err);
      setError('Failed to initialize workspace');
      setIsInitialized(true);
    }
  }, [userSettings, profilesData, currentWorkspace, dispatch]);

  // Initialize workspace when data is available
  useEffect(() => {
    if (!isSettingsLoading && !isProfilesLoading) {
      initializeWorkspace();
    }
  }, [isSettingsLoading, isProfilesLoading, initializeWorkspace]);

  // Handle workspace switching
  const switchWorkspace = useCallback(async (workspaceMongoId: string) => {
    if (!workspaceMongoId || workspaceMongoId === currentWorkspace) {
      return;
    }

    try {
      setError(null);

      // Find the workspace from profilesData to get the workspace name for display
      const targetWorkspace = profilesData?.workspaces?.find((ws: Workspace) => ws._id === workspaceMongoId);
      const workspaceName = targetWorkspace?.workspace_id || workspaceMongoId;

      // Update backend first
      console.log('About to call updateWorkspace with MongoDB ID:', workspaceMongoId);

      const response = await updateWorkspace({
        current_workspace: workspaceMongoId
      }).unwrap();

      console.log('Workspace switch response:', response);

      // Update local state with the MongoDB ObjectId
      setCurrentWorkspace(workspaceMongoId);
      localStorage.setItem('workspace_id', workspaceMongoId);
      dispatch(workSpaceId(workspaceMongoId));

      // Show success toast with workspace name from backend response
      const displayName = response.workspace_name || workspaceName;
      toast.success(`Switched to workspace: ${displayName}`);

      // Refresh profiles to get updated integration status
      console.log('Refreshing profiles after workspace switch');
      refetchProfiles();

    } catch (err: any) {
      console.error('Error switching workspace:', err);
      const errorMessage = err?.data?.error || err?.message || 'Failed to switch workspace';
      setError(errorMessage);
      toast.error(`Error switching workspace: ${errorMessage}`);
    }
  }, [currentWorkspace, updateWorkspace, dispatch, refetchProfiles, profilesData]);

  // Refresh workspaces
  const refreshWorkspaces = useCallback(() => {
    refetchProfiles();
  }, [refetchProfiles]);

  // Get current workspace ObjectId
  const getCurrentWorkspaceObjectId = useCallback((): string | null => {
    // currentWorkspace is now the MongoDB ObjectId
    return currentWorkspace;
  }, [currentWorkspace]);

  // Get current workspace display name
  const getCurrentWorkspaceName = useCallback((): string | null => {
    if (!currentWorkspace || !profilesData?.workspaces) {
      return null;
    }

    // Find the workspace by ObjectId and return its display name
    const workspace = profilesData.workspaces.find((ws: Workspace) => ws._id === currentWorkspace);
    return workspace?.workspace_id || null;
  }, [currentWorkspace, profilesData]);

  // Handle errors
  useEffect(() => {
    if (profilesError) {
      console.error('Profiles error:', profilesError);
      setError('Failed to load workspaces');
    }
  }, [profilesError]);

  // Persist workspace changes to localStorage when currentWorkspace changes
  useEffect(() => {
    if (currentWorkspace && isInitialized) {
      localStorage.setItem('workspace_id', currentWorkspace);
      dispatch(workSpaceId(currentWorkspace));
    }
  }, [currentWorkspace, isInitialized, dispatch]);

  // Debug workspace states
  useEffect(() => {
    console.log('WorkspaceContext states:', {
      isProfilesLoading,
      isSettingsLoading,
      isUpdatingWorkspace,
      updateWorkspaceError,
      totalIsLoading: isProfilesLoading || isSettingsLoading || isUpdatingWorkspace
    });
  }, [isProfilesLoading, isSettingsLoading, isUpdatingWorkspace, updateWorkspaceError]);

  const contextValue: WorkspaceContextType = {
    currentWorkspace,
    workspaces: profilesData?.workspaces || [],
    switchWorkspace,
    refreshWorkspaces,
    isLoading: isProfilesLoading || isSettingsLoading || isUpdatingWorkspace,
    error,
    isInitialized,
    getCurrentWorkspaceObjectId,
    getCurrentWorkspaceName,
  };

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

// Hook for getting current workspace ID (backward compatibility)
export const useCurrentWorkspace = (): string | null => {
  const { currentWorkspace } = useWorkspace();
  return currentWorkspace;
};

// Hook for workspace switching (backward compatibility)
export const useWorkspaceSwitcher = () => {
  const { switchWorkspace, isLoading } = useWorkspace();
  return { switchWorkspace, isLoading };
};

export default WorkspaceContext;